import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Shield, BarChart, Target, Palette, Settings, Info } from 'lucide-react';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import { CookieConsentData } from '@/utils/cookieConsent';

interface CookieSettingsProps {
  trigger?: React.ReactNode;
}

export function CookieSettings({ trigger }: CookieSettingsProps) {
  const {
    consent,
    saveCustomConsent,
    cookieDescriptions,
    acceptAll,
    acceptNecessary
  } = useCookieConsent();

  const [open, setOpen] = useState(false);
  const [customConsent, setCustomConsent] = useState({
    necessary: true,
    analytics: consent?.analytics ?? false,
    marketing: consent?.marketing ?? false,
    preferences: consent?.preferences ?? true
  });

  const handleSave = () => {
    saveCustomConsent(customConsent as Omit<CookieConsentData, 'timestamp' | 'version'>);
    setOpen(false);
  };

  const categoryIcons = {
    necessary: Shield,
    analytics: BarChart,
    marketing: Target,
    preferences: Palette
  };

  const defaultTrigger = (
    <Button variant="ghost" size="sm" className="text-muted-foreground">
      <Settings className="h-4 w-4 mr-2" />
      Cookies
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || defaultTrigger}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Paramètres des cookies
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="bg-muted/50 p-4 rounded-lg">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-500 mt-0.5" />
              <div>
                <h3 className="font-medium text-sm mb-1">À propos des cookies</h3>
                <p className="text-xs text-muted-foreground">
                  Les cookies sont de petits fichiers texte stockés sur votre appareil lors de votre visite sur notre site. 
                  Ils nous aident à améliorer votre expérience et à analyser l'utilisation du site.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {Object.entries(cookieDescriptions).map(([key, desc]) => {
              const Icon = categoryIcons[key as keyof typeof categoryIcons];
              const isNecessary = key === 'necessary';
              const isEnabled = customConsent[key as keyof typeof customConsent];

              return (
                <Card key={key} className="relative">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Icon className="h-4 w-4" />
                      {desc.title}
                      {isNecessary && (
                        <Badge variant="secondary" className="text-xs">
                          Toujours actif
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-muted-foreground mb-3">
                      {desc.description}
                    </p>
                    
                    <div className="flex flex-wrap gap-1 mb-4">
                      {desc.examples.map((example, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {example}
                        </Badge>
                      ))}
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">
                        {isEnabled ? 'Autorisé' : 'Bloqué'}
                      </span>
                      <Button
                        variant={isEnabled ? "default" : "outline"}
                        size="sm"
                        disabled={isNecessary}
                        onClick={() => {
                          if (!isNecessary) {
                            setCustomConsent(prev => ({
                              ...prev,
                              [key]: !prev[key as keyof typeof prev]
                            }));
                          }
                        }}
                      >
                        {isEnabled ? 'Désactiver' : 'Activer'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {consent && (
            <div className="bg-muted/30 p-3 rounded-lg">
              <p className="text-xs text-muted-foreground">
                Dernière modification : {new Date(consent.timestamp).toLocaleDateString('fr-FR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
            <Button onClick={acceptAll} className="flex-1">
              Accepter tout
            </Button>
            <Button onClick={handleSave} variant="outline" className="flex-1">
              Sauvegarder les préférences
            </Button>
            <Button onClick={acceptNecessary} variant="ghost" className="flex-1">
              Nécessaires uniquement
            </Button>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            Ces paramètres sont sauvegardés localement et expireront après 1 an.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
